import { getDataFromObject, setDataToObj } from "utils/common";
import { FIELD_TYPES } from "../constants";
import dayjs from "dayjs";

// Define a type for the field types based on FIELD_TYPES
type FieldType = keyof typeof FIELD_TYPES;

type Field = {
    type: string;
    subType?: string;
    name: string;
    children?: Field[];
    fieldType?: FieldType;
    fieldProps?: Record<string, any>;
    validations?: object;
    isRequired?: boolean;
};

type Schema = Field[];

export const printDate = (date: Date | string, format: string = "DD-MM-YYYY hh:mm A"): string =>
    dayjs(date).format(format);

export const getDefaultFormValue = (schema: Schema | null, defaultValue: Record<string, any> | false = false): Record<string, any> => {
    if (!schema || !Array.isArray(schema)) return {};
    const fieldsArr = getArrayOfFields(schema);

    const fields = fieldsArr.reduce((prev: any, next:any) => {
        const { type, subType = "", name, children = [] } = next;
        let defaultVal = defaultValue ? getDataFromObject(defaultValue, name) : null;

        if (!defaultVal) {
            defaultVal =
                type === "fieldArray" && subType === "field"
                    ? [getDefaultFieldValue(next)]
                    : type === "fieldArray" && subType === "section"
                    ? [getDefaultFormValue(children)]
                    : getDefaultFieldValue(next);
        }

        return setDataToObj(prev, name, defaultVal);
    }, {});

    return fields;
};

export const getDefaultFieldValue = (field: Field): any => {
    const { fieldType, fieldProps = {} } = field;

    if ([FIELD_TYPES.dateRange, FIELD_TYPES.timeRange].includes(fieldType as any)) {
        return [null, null];
    }
    if (
        [FIELD_TYPES.tags, FIELD_TYPES.checkbox].includes(fieldType as any) ||
        (fieldType === FIELD_TYPES.select && fieldProps.multiple) ||
        (fieldType === FIELD_TYPES.fileUpload && fieldProps.multiple)
    ) {
        return [];
    }
    if ([FIELD_TYPES.select, FIELD_TYPES.fileUpload].includes(fieldType as any)) {
        return null;
    }
    if ([FIELD_TYPES.switch].includes(fieldType as any)) {
        return false;
    }
    return "";
};

export const checkIfFieldIsRequired = (field: Field): boolean => {
    const { isRequired, validations = {}, fieldType, fieldProps = {} } = field;

    if (isRequired || validations.hasOwnProperty("required")) {
        return true;
    }
    if (
        ([
            FIELD_TYPES.tags,
            FIELD_TYPES.fileUpload,
            FIELD_TYPES.checkbox,
            FIELD_TYPES.dateRange,
            FIELD_TYPES.timeRange,
        ].includes(fieldType as any) || 
        (fieldType === FIELD_TYPES.select && fieldProps.multiple)) &&
        validations.hasOwnProperty("min")
    ) {
        return true;
    }
    return false;
};

export const getArrayOfFields = (schema: Schema): Field[] => {
    return schema.reduce<Field[]>((prev, next) => {
        if (next.type === "row") {
            prev.push(
                ...next.children!.filter(
                    (f) =>
                        f.type === "field" ||
                        (f.type === "fieldArray" && f?.subType === "field")
                )
            );
        }
        if (next.type === "field" || next.type === "fieldArray") {
            prev.push(next);
        }
        return prev;
    }, []);
};
